'use client'

/**
 * Client registration — BFF POST /api/auth/zitadel/client/register
 *
 * Visual: same dual-plane craft floor as ClientLoginPage (espresso rail + sand bay).
 * UX: ui-ux-pro-max — visible labels, blur validation, ≥44px targets, inline errors,
 * submit loading/success feedback, focus recovery, trust row, reduced-motion.
 * API payload and endpoint must stay unchanged.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { Button, Input, Form } from 'antd'
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CloseOutlined,
  ArrowRightOutlined,
  LockOutlined,
  MailOutlined,
  UserOutlined,
  LineChartOutlined,
  FundOutlined,
  SwapOutlined,
  RobotOutlined,
} from '@ant-design/icons'
import { useTheme } from '@/shared/theme'
import { AuthNavbar } from '@/domains/auth/components/AuthNavbar'
import '@/shared/theme/styles/login.css'
import desk from './ClientLoginPage.module.css'
import reg from './ClientRegisterPage.module.css'

/** Free Desk contents — shipped runtime truth. */
const VALUE_LINES = [
  { icon: <LineChartOutlined aria-hidden />, text: 'Market and portfolio views' },
  { icon: <RobotOutlined aria-hidden />, text: 'One named agent, always labeled PAPER' },
  { icon: <FundOutlined aria-hidden />, text: '200 paper-only Desk Credits each month' },
  { icon: <SwapOutlined aria-hidden />, text: 'Research chat metered by Desk Credits' },
] as const

/** Token-friendly strength levels (no purple marketing CTA palette). */
const getPasswordStrength = (
  pwd: string
): { level: number; text: string; tone: 'muted' | 'danger' | 'warn' | 'info' | 'success' } => {
  if (!pwd) return { level: 0, text: 'Enter a password', tone: 'muted' }
  let score = 0
  if (pwd.length >= 8) score++
  if (pwd.length >= 12) score++
  if (/[A-Z]/.test(pwd)) score++
  if (/[a-z]/.test(pwd)) score++
  if (/[0-9]/.test(pwd)) score++
  if (/[^A-Za-z0-9]/.test(pwd)) score++
  if (score <= 2) return { level: 25, text: 'Weak', tone: 'danger' }
  if (score <= 3) return { level: 50, text: 'Fair', tone: 'warn' }
  if (score <= 4) return { level: 75, text: 'Good', tone: 'info' }
  return { level: 100, text: 'Strong', tone: 'success' }
}

function parseCheckoutIntent(returnTo: string | null): string | null {
  if (!returnTo || !returnTo.startsWith('/') || returnTo.startsWith('//')) return null
  try {
    const u = new URL(returnTo, 'https://local.invalid')
    if (u.pathname !== '/pricing') return null
    const plan = u.searchParams.get('plan')
    if (plan === 'starter') return 'Operator'
    if (plan === 'advanced') return 'Desk'
    if (plan === 'professional') return 'Pro'
    if (u.searchParams.get('resume') === 'checkout') return 'Paid plan'
  } catch {
    /* no-op */
  }
  return null
}

function RegisterBrandPanel() {
  return (
    <aside
      className={`${desk.brandPanel} ${reg.brandPanelFix}`}
      aria-label="Mechagora client registration"
    >
      <div className={desk.brandField} aria-hidden>
        <span className={`${desk.brandRing} ${desk.brandRingOuter}`} />
        <span className={`${desk.brandRing} ${desk.brandRingMid}`} />
        <span className={`${desk.brandRing} ${desk.brandRingInner}`} />
        <div className={desk.brandMedallion}>
          <span>M</span>
        </div>
      </div>

      <div className={desk.brandContent}>
        <div className={desk.brandTop}>
          <p className={desk.brandMeta}>
            <span className={desk.brandMetaTag}>Client</span>
            <span className={desk.brandMetaSep} aria-hidden>
              /
            </span>
            <span>New account</span>
          </p>
          <h2 className={desk.brandHeading}>
            Open Free Desk
            <br />
            <span className={desk.brandHeadingAccent}>Start with a paper agent.</span>
          </h2>
          <p className={desk.brandDescription}>
            Create a client account, name your paper agent after sign-in, and enter a clearly
            labeled paper workspace. Upgrade only when you need live controls.
          </p>
        </div>

        <div className={desk.valueBlock}>
          <p className={desk.valueBlockLabel}>Included on Free Desk</p>
          <ul className={desk.valueList}>
            {VALUE_LINES.map((line) => (
              <li key={line.text} className={desk.valueLine}>
                <span className={desk.valueIcon}>{line.icon}</span>
                <span className={desk.valueText}>{line.text}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className={desk.brandFoot}>
          PAPER only. No live agents or live order execution.
        </p>
      </div>
    </aside>
  )
}

const ClientRegisterPage: React.FC = () => {
  const router = useRouter()
  const searchParams = useSearchParams()
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [passwordValue, setPasswordValue] = useState('')
  const [isMobile, setIsMobile] = useState(false)
  const theme = useTheme()
  const dark = theme.isDark
  const [form] = Form.useForm()
  const errorRef = useRef<HTMLDivElement>(null)
  const firstNameRef = useRef<HTMLInputElement | null>(null)

  const returnTo = searchParams.get('return_to')
  const postRegisterLoginHref = useMemo(() => {
    if (returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//')) {
      return `/auth/client-login?return_to=${encodeURIComponent(returnTo)}`
    }
    return '/auth/client-login'
  }, [returnTo])

  const checkoutPlanLabel = useMemo(() => parseCheckoutIntent(returnTo), [returnTo])
  const passwordStrength = getPasswordStrength(passwordValue)

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 900)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  useEffect(() => {
    if (!error) return
    const t = window.setTimeout(() => errorRef.current?.focus(), 0)
    return () => window.clearTimeout(t)
  }, [error])

  const handleRegister = async (values: {
    firstName: string
    lastName?: string
    email: string
    password: string
    confirmPassword: string
  }) => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetch('/api/auth/zitadel/client/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: values.firstName,
          lastName: values.lastName ?? '',
          email: values.email,
          password: values.password,
        }),
      })
      const data = (await res.json()) as { success?: boolean; error?: string }
      if (!res.ok || !data.success) {
        setError(data.error ?? 'Registration failed. Please try again.')
      } else {
        setSuccess(true)
        window.setTimeout(() => router.push(postRegisterLoginHref), 2200)
      }
    } catch {
      setError('A network error occurred. Check your connection and try again.')
    } finally {
      setLoading(false)
    }
  }

  const focusFirstInvalid = () => {
    const fields = ['firstName', 'lastName', 'email', 'password', 'confirmPassword'] as const
    for (const name of fields) {
      if (form.getFieldError(name).length) {
        form.scrollToField(name, { behavior: 'smooth', block: 'center' })
        if (name === 'firstName') firstNameRef.current?.focus()
        return
      }
    }
  }

  return (
    <div
      className={`login-page ${dark ? 'dark' : 'light'}`}
      data-theme={dark ? 'dark' : 'light'}
    >
      <AuthNavbar />

      <div className={desk.body}>
        <div className={desk.canvasBrand} aria-hidden>
          <div className={desk.canvasVignette} />
          <div className={desk.canvasOrbA} />
          <div className={desk.canvasOrbB} />
          <div className={desk.canvasMark}>M</div>
          <div className={desk.canvasWord}>MECHAGORA</div>
        </div>

        <div className={desk.workspaceShell}>
          {!isMobile && <RegisterBrandPanel />}

          <main className={`${desk.formSide} ${reg.formSideScroll}`} id="main-content">
            <div className={desk.formCard}>
              <div className={desk.authBezel}>
                <span className={desk.authBezelLeft}>
                  <span className={desk.bezelMark} aria-hidden />
                  <span className={desk.authBezelLabel}>auth · register</span>
                </span>
                <span
                  className={`${desk.authBezelStatus} ${success ? desk.authBezelReady : loading ? '' : desk.authBezelReady
                    }`}
                >
                  {success ? 'created' : loading ? 'working' : 'open'}
                </span>
              </div>

              <div className={desk.formContainer}>
                {/* Single h1 — left-aligned desktop; centered only on narrow via reg CSS */}
                <header className={`${desk.formHeader} ${reg.formHeader}`}>
                  <p className={desk.formKicker}>Free Desk · client account</p>
                  <h1 className={desk.formTitle}>Open Free Desk</h1>
                  <p className={desk.formSubtitle}>
                    One named PAPER agent, market and portfolio views, research chat, and 200
                    paper-only Desk Credits. No live execution.
                  </p>
                </header>

                {checkoutPlanLabel ? (
                  <div className={reg.intentChip} role="status">
                    <span>
                      After sign-in you can continue <strong>{checkoutPlanLabel}</strong> checkout.
                    </span>
                  </div>
                ) : null}

                {error ? (
                  <div
                    ref={errorRef}
                    className={desk.errorAlert}
                    role="alert"
                    tabIndex={-1}
                    id="register-form-error"
                  >
                    <ExclamationCircleOutlined className={desk.errorIcon} aria-hidden />
                    <div className={desk.errorContent}>
                      <div className={desk.errorTitle}>Could not create account</div>
                      <div className={desk.errorText}>{error}</div>
                    </div>
                    <Button
                      type="text"
                      size="small"
                      icon={<CloseOutlined />}
                      onClick={() => setError(null)}
                      className={desk.dismissBtn}
                      aria-label="Dismiss error"
                    />
                  </div>
                ) : null}

                {success ? (
                  <div className={reg.successAlert} role="status" aria-live="polite">
                    <CheckCircleOutlined aria-hidden />
                    <span>Free Desk created. Sign in next to name your paper agent…</span>
                  </div>
                ) : null}

                {!success ? (
                  <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleRegister}
                    onFinishFailed={focusFirstInvalid}
                    scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
                    requiredMark={false}
                    className={desk.formFields}
                    disabled={loading}
                    aria-busy={loading}
                  >
                    <div className={reg.nameGrid}>
                      <Form.Item
                        name="firstName"
                        htmlFor="client-register-firstName"
                        label={<span className={desk.fieldLabel}>First name</span>}
                        rules={[{ required: true, message: 'Enter your first name' }]}
                        validateTrigger={['onBlur', 'onSubmit']}
                      >
                        <Input
                          ref={(node) => {
                            firstNameRef.current = node?.input ?? null
                          }}
                          prefix={<UserOutlined aria-hidden />}
                          id="client-register-firstName"
                          name="firstName"
                          placeholder="Jane"
                          autoComplete="given-name"
                          aria-required="true"
                          aria-describedby={error ? 'register-form-error' : undefined}
                        />
                      </Form.Item>
                      <Form.Item
                        name="lastName"
                        htmlFor="client-register-lastName"
                        label={<span className={desk.fieldLabel}>Last name</span>}
                        rules={[]}
                        validateTrigger={['onBlur', 'onSubmit']}
                      >
                        <Input
                          id="client-register-lastName"
                          name="lastName"
                          placeholder="Doe"
                          autoComplete="family-name"
                        />
                      </Form.Item>
                    </div>

                    <Form.Item
                      name="email"
                      htmlFor="client-register-email"
                      label={<span className={desk.fieldLabel}>Email</span>}
                      extra={
                        <span className={reg.fieldHelp}>
                          Work email preferred — used for sign-in and account recovery.
                        </span>
                      }
                      rules={[
                        { required: true, message: 'Enter your email address' },
                        { type: 'email', message: 'Enter a valid email address' },
                      ]}
                      validateTrigger={['onBlur', 'onSubmit']}
                    >
                      <Input
                        prefix={<MailOutlined aria-hidden />}
                        type="email"
                        id="client-register-email"
                        name="email"
                        placeholder="you@company.com"
                        autoComplete="email"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck={false}
                        inputMode="email"
                        aria-required="true"
                      />
                    </Form.Item>

                    <Form.Item
                      name="password"
                      htmlFor="client-register-password"
                      label={<span className={desk.fieldLabel}>Password</span>}
                      rules={[
                        { required: true, message: 'Enter a password' },
                        { min: 8, message: 'Use at least 8 characters' },
                      ]}
                      validateTrigger={['onBlur', 'onSubmit']}
                      style={{ marginBottom: 8 }}
                    >
                      <Input.Password
                        prefix={<LockOutlined aria-hidden />}
                        id="client-register-password"
                        name="password"
                        placeholder="At least 8 characters"
                        autoComplete="new-password"
                        aria-required="true"
                        aria-describedby="password-strength-status"
                        onChange={(e) => setPasswordValue(e.target.value)}
                      />
                    </Form.Item>

                    <div
                      className={`${reg.strengthPanel} ${reg.strengthTone} ${reg[`tone_${passwordStrength.tone}`]}`}
                      id="password-strength-status"
                      aria-live="polite"
                    >
                      <div className={reg.strengthHeader}>
                        <span className={reg.strengthLabel}>Password strength</span>
                        <span className={reg.strengthValue}>{passwordStrength.text}</span>
                      </div>
                      <div className={reg.strengthBars} aria-hidden>
                        {[0, 1, 2, 3].map((i) => (
                          <div
                            key={i}
                            className={`${reg.strengthBar} ${passwordStrength.level > i * 25 ? reg.strengthBarOn : ''
                              }`}
                          />
                        ))}
                      </div>
                    </div>

                    <Form.Item
                      name="confirmPassword"
                      htmlFor="client-register-confirm"
                      label={<span className={desk.fieldLabel}>Confirm password</span>}
                      dependencies={['password']}
                      rules={[
                        { required: true, message: 'Confirm your password' },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (!value || getFieldValue('password') === value) {
                              return Promise.resolve()
                            }
                            return Promise.reject(new Error('Passwords do not match'))
                          },
                        }),
                      ]}
                      validateTrigger={['onBlur', 'onSubmit']}
                    >
                      <Input.Password
                        prefix={<LockOutlined aria-hidden />}
                        id="client-register-confirm"
                        name="confirmPassword"
                        placeholder="Re-enter password"
                        autoComplete="new-password"
                        aria-required="true"
                      />
                    </Form.Item>

                    <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
                      <Button
                        type="primary"
                        block
                        htmlType="submit"
                        icon={!loading ? <ArrowRightOutlined /> : undefined}
                        iconPlacement="end"
                        loading={loading}
                        className={`${desk.submitBtn} ${reg.denseBtn}`}
                        aria-disabled={loading}
                      >
                        {loading ? 'Opening Free Desk…' : 'Open Free Desk'}
                      </Button>
                    </Form.Item>
                  </Form>
                ) : null}

                <div className={desk.divider} role="separator">
                  <div className={desk.dividerLine} />
                  <span className={desk.dividerText}>already have an account?</span>
                  <div className={desk.dividerLine} />
                </div>

                <div className={reg.actionsStack}>
                  <Button
                    type="default"
                    block
                    className={`${desk.secondaryBtn} ${reg.denseBtn}`}
                    onClick={() => {
                      router.push(postRegisterLoginHref)
                    }}
                    disabled={loading}
                  >
                    Sign in instead
                  </Button>
                </div>

                <p className={reg.formFootnote}>
                  By creating an account you agree to our{' '}
                  <a href="/legal/terms" className={reg.legalLink}>
                    Terms
                  </a>{' '}
                  and{' '}
                  <a href="/legal/privacy" className={reg.legalLink}>
                    Privacy&nbsp;Policy
                  </a>
                  . See also{' '}
                  <a href="/legal/disclosures" className={reg.legalLink}>
                    Disclosures
                  </a>
                  .
                </p>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  )
}

export default ClientRegisterPage
